import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Copy, Check, Share2, Instagram, Facebook, Twitter, Smartphone } from "lucide-react";

const COACH_INSTAGRAM_URL = "https://instagram.com/flow_state_method";
const COACH_INSTAGRAM_HANDLE = "@flow_state_method";

/**
 * Builds the share text used across every share path so the message is consistent:
 * X intent, Web Share API, clipboard copy, and the "open in Instagram" fallback.
 */
export function buildShareText(story) {
  const author = story.user_name || "A community member";
  const headline = story.headline || "How boxing changed my life";
  return `${headline}\n\n${author} on Better Through Boxing.\n\nFollow Coach Chris: ${COACH_INSTAGRAM_HANDLE}`;
}

export function buildShareUrl(story) {
  // Point at the backend OG route so scrapers (LinkedIn/FB/X/iMessage) get a rich
  // card; it instantly redirects human browsers to /story/:id. Same origin in prod.
  const base = typeof window !== "undefined" ? window.location.origin : "https://betterthroughboxing.com";
  return `${base}/api/share/story/${story.id}`;
}

export function StoryShareButton({ story, className = "", ...rest }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest px-3 py-2 border border-[#27272A] hover:border-[#FF331F] hover:text-[#FF331F] text-zinc-300 transition-colors ${className}`}
        data-testid={`story-share-btn-${story.id}`}
        {...rest}
      >
        <Share2 className="h-3.5 w-3.5" /> Share
      </button>
      <StoryShareDialog open={open} onClose={() => setOpen(false)} story={story} />
    </>
  );
}

export function StoryShareDialog({ open, onClose, story }) {
  const [copied, setCopied] = useState(false);
  if (!story) return null;
  const url = buildShareUrl(story);
  const text = buildShareText(story);
  const fullCaption = `${text}\n\n${url}`;
  const canNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(fullCaption);
      setCopied(true);
      toast.success("Caption + link copied — paste it anywhere");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — long-press the link instead");
    }
  };

  const nativeShare = async () => {
    try {
      await navigator.share({ title: story.headline, text, url });
    } catch {
      /* user cancelled — silent */
    }
  };

  const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`;

  const openInstagram = async () => {
    // Instagram has no public web-share intent. Copy the caption + URL so the
    // user can paste it into their post/story, then deep-link the app.
    try {
      await navigator.clipboard.writeText(fullCaption);
      toast.success("Caption + link copied — paste in your IG post or story");
    } catch {
      toast.message("Copy the caption from the box below into Instagram");
    }
    // Try the app via the deep link, fall back to web in 600ms.
    const ts = Date.now();
    window.location.href = "instagram://camera";
    setTimeout(() => {
      if (Date.now() - ts < 1200) window.open(COACH_INSTAGRAM_URL, "_blank", "noopener");
    }, 600);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-[#0F0F0F] border-[#27272A] text-white" data-testid="story-share-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl">Share this story.</DialogTitle>
          <DialogDescription className="text-zinc-500">
            Spread the message and help someone start. Every share includes a link to {COACH_INSTAGRAM_HANDLE}.
          </DialogDescription>
        </DialogHeader>

        {/* Native share — mobile sheet (iOS/Android) */}
        {canNativeShare && (
          <Button
            onClick={nativeShare}
            className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm h-11"
            data-testid="story-share-native"
          >
            <Smartphone className="h-4 w-4 mr-2" /> Share via your device
          </Button>
        )}

        {/* Per-platform buttons */}
        <div className="grid grid-cols-3 gap-2">
          <a
            href={tweetUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex flex-col items-center justify-center gap-1 border border-[#27272A] hover:border-[#FF331F] hover:text-[#FF331F] text-zinc-300 py-3 transition-colors"
            data-testid="story-share-twitter"
          >
            <Twitter className="h-4 w-4" />
            <span className="text-[10px] uppercase tracking-widest font-bold">X / Twitter</span>
          </a>
          <a
            href={facebookUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex flex-col items-center justify-center gap-1 border border-[#27272A] hover:border-[#FF331F] hover:text-[#FF331F] text-zinc-300 py-3 transition-colors"
            data-testid="story-share-facebook"
          >
            <Facebook className="h-4 w-4" />
            <span className="text-[10px] uppercase tracking-widest font-bold">Facebook</span>
          </a>
          <button
            type="button"
            onClick={openInstagram}
            className="inline-flex flex-col items-center justify-center gap-1 border border-[#27272A] hover:border-[#FF331F] hover:text-[#FF331F] text-zinc-300 py-3 transition-colors"
            data-testid="story-share-instagram"
          >
            <Instagram className="h-4 w-4" />
            <span className="text-[10px] uppercase tracking-widest font-bold">Instagram</span>
          </button>
        </div>

        {/* Link + caption preview with copy */}
        <div className="space-y-2 border-t border-[#27272A] pt-4">
          <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">Story link</div>
          <div className="flex gap-2">
            <Input
              readOnly
              value={url}
              className="bg-[#050505] border-[#27272A] rounded-sm text-xs font-mono"
              data-testid="story-share-url"
              onFocus={(e) => e.target.select()}
            />
            <Button
              onClick={copyLink}
              className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm flex-shrink-0"
              data-testid="story-share-copy"
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
          <p className="text-[10px] text-zinc-500 leading-relaxed">
            Tap copy to grab the link <span className="text-zinc-400">+</span> the caption (auto-includes {COACH_INSTAGRAM_HANDLE}) so it&apos;s ready to paste anywhere.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
